import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dismissalOrders, employees } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DismissalOrderPrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['dismissal-order', id ?? ''],
    queryFn: () => dismissalOrders.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const empQ = useQuery({
    queryKey: ['employee-full', docQ.data?.employee_id ?? ''],
    queryFn: () => employees.get(docQ.data!.employee_id),
    enabled: !!docQ.data,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  if (!docQ.data || !empQ.data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const d = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === d.organization_id);

  return (
    <div className="print-page" style={{
      padding: '15mm', fontFamily: 'Times, serif', fontSize: '10pt',
      maxWidth: '210mm', margin: '0 auto', background: 'var(--bg-panel)', lineHeight: 1.4,
    }}>
      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.dismissal_orders',
        documentId: d.id,
        filename: `Наказ про звільнення Т-8 ${d.document_number ?? ''}`.trim(),
        orgName: org?.name ?? null,
      }} />

      <div style={{ textAlign: 'center' }}>
        <div>{org?.full_name ?? org?.name ?? ''}</div>
        {partyCodesText(org) && <div style={{ fontSize: '9pt' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '14pt', margin: '1.5rem 0 0.3rem' }}>
        НАКАЗ № {d.document_number}
      </h2>
      <p style={{ textAlign: 'center', margin: 0 }}>
        від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </p>
      <p style={{ textAlign: 'center', margin: '0.5rem 0', fontWeight: 600 }}>
        про припинення трудового договору (звільнення)
      </p>

      <p style={{ marginTop: '1.5rem' }}>
        Звільнити <strong>{empQ.data.full_name}</strong>
        {empQ.data.code ? ` (таб. № ${empQ.data.code})` : ''}
        {empQ.data.position_name ? `, ${empQ.data.position_name}` : ''}
        {empQ.data.department_name ? ` (${empQ.data.department_name})` : ''} з&nbsp;
        <strong>{new Date(d.dismiss_date).toLocaleDateString('uk-UA')}</strong>.
      </p>

      {d.reason_article && (
        <p style={{ marginTop: '0.5rem' }}>
          <strong>Підстава:</strong> {d.reason_article}
        </p>
      )}
      {d.reason && (
        <p style={{ marginTop: '0.5rem' }}>
          <strong>Причина:</strong> {d.reason}
        </p>
      )}

      {Number(d.compensation_amount) > 0 && (
        <p style={{ marginTop: '0.5rem' }}>
          Виплатити компенсацію за невикористану відпустку у сумі&nbsp;
          <strong>{fmt2(d.compensation_amount)}</strong> грн.
        </p>
      )}

      <table style={{ width: '100%', marginTop: '3rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%' }}>
              <div>Керівник:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>(підпис, П.І.Б.)</div>
            </td>
            <td style={{ width: '50%' }}>
              <div>З наказом ознайомлений:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>
                (підпис працівника, дата)
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <style>{`
        @media print {
          .print-hide { display: none !important; }
        }
      `}</style>
    </div>
  );
}
